/**
 * Shim for @loadable/component to work with Vite
 *
 * This deliberately does NOT use `React.lazy`: `lazy` throws a promise, and a component that
 * suspends while `hydrateRoot` is walking the SSR markup leaves React's hydration cursor stale.
 * Every sibling claimed afterwards then fails with "Hydration failed because the initial UI does
 * not match what was rendered on the server" and the whole root is torn down — `@s-ui/react-atom-popover`
 * (one of these per ad card) took down every AdsList route that way. The real `@loadable/component`
 * renders `fallback` and loads from an effect instead, which is what we reproduce here.
 */
import {createElement, forwardRef, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

const resolveComponent = module => {
  if (module?.default && typeof module.default === 'function') return module.default
  if (module?.default && typeof module.default === 'object' && module.default.default) return module.default.default
  if (typeof module === 'function') return module
  return module?.default ?? module
}

// Main loadable function — mirrors @loadable/component's non-suspending behaviour
export default function loadable(loadFn, options = {}) {
  let LoadedComponent = null
  let pending = null

  const load = () => {
    pending =
      pending ??
      Promise.resolve(loadFn()).then(module => {
        LoadedComponent = resolveComponent(module)
        return LoadedComponent
      })
    return pending
  }

  const Loadable = forwardRef(({fallback, ...props}, ref) => {
    // A component IS a function, so it must be wrapped on the way in and out of state — React would
    // otherwise run it as a lazy initializer / updater and render its return value instead.
    const [Component, setComponent] = useState(() => LoadedComponent)

    useEffect(() => {
      if (Component !== null) return undefined
      let cancelled = false
      load().then(Resolved => {
        if (!cancelled) setComponent(() => Resolved)
      })
      return () => {
        cancelled = true
      }
    }, [Component])

    if (Component === null) return fallback ?? options.fallback ?? null

    return createElement(Component, {...props, ref})
  })

  Loadable.displayName = 'Loadable'

  Loadable.propTypes = {
    fallback: PropTypes.node
  }

  return Loadable
}

// Named exports
export {default as loadableReady} from './loadableReady.js'
export {default as lazy} from './lazy.js'
